import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Helper function to create a title page
const createTitlePage = (pdf, userName) => {
  const pageWidth = pdf.internal.pageSize.width;
  const pageHeight = pdf.internal.pageSize.height;
  
  // Title
  pdf.setFontSize(36);
  pdf.setFont(undefined, 'bold');
  pdf.text('My Recipe Book', pageWidth / 2, pageHeight / 3, { align: 'center' });
  
  // Subtitle
  pdf.setFontSize(18);
  pdf.setFont(undefined, 'normal');
  pdf.text(`${userName || 'Personal'} Cookbook Collection`, pageWidth / 2, pageHeight / 3 + 20, { align: 'center' });
  
  // Date
  pdf.setFontSize(14);
  const date = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
  pdf.text(`Generated on ${date}`, pageWidth / 2, pageHeight / 3 + 40, { align: 'center' });
  
  // Footer
  pdf.setFontSize(10);
  pdf.text('Created with PlatePassion.com', pageWidth / 2, pageHeight - 30, { align: 'center' });
};

// Helper function to create a course divider page
const createCourseDivider = (pdf, courseName) => {
  const pageWidth = pdf.internal.pageSize.width;
  const pageHeight = pdf.internal.pageSize.height;
  
  // Course name
  pdf.setFontSize(48);
  pdf.setFont(undefined, 'bold');
  pdf.text(courseName, pageWidth / 2, pageHeight / 2, { align: 'center' });
  
  // Decorative line
  pdf.setLineWidth(2);
  pdf.line(50, pageHeight / 2 + 15, pageWidth - 50, pageHeight / 2 + 15);
};

// Helper function to get the proper image URL (proxy for S3)
const getProxiedImageUrl = (url) => {
  if (!url) return null;
  
  // For S3 URLs, use a proxy endpoint to avoid CORS issues
  if (url.includes('s3.amazonaws.com')) {
    return `${process.env.REACT_APP_API_URL}/api/images/proxy?url=${encodeURIComponent(url)}`;
  }
  
  return url;
};

// Helper function to render recipe HTML for PDF
const createRecipeHTML = async (recipe, includeImages) => {
  const container = document.createElement('div');
  container.style.width = '800px';
  container.style.padding = '40px';
  container.style.backgroundColor = 'white';
  container.style.fontFamily = 'Arial, sans-serif';
  container.style.position = 'fixed';
  container.style.top = '-9999px';
  container.style.left = '-9999px';
  
  let html = `
    <div style="margin-bottom: 30px;">
      <h1 style="color: #333; margin-bottom: 10px; font-size: 28px;">${recipe.name}</h1>
      <div style="color: #666; font-size: 14px; margin-bottom: 20px;">
        ${recipe.course ? `<span>Course: ${recipe.course}</span> | ` : ''}
        <span>Servings: ${recipe.servings}</span>
        ${recipe.serving_size ? ` | <span>Serving Size: ${recipe.serving_size}</span>` : ''}
      </div>
      ${recipe.description ? `<p style="color: #555; margin-bottom: 20px;">${recipe.description}</p>` : ''}
    </div>
  `;

  // Add image if available and requested
  if (includeImages && recipe.image_url) {
    const proxiedUrl = getProxiedImageUrl(recipe.image_url);
    
    if (proxiedUrl) {
      html += `
        <div style="margin-bottom: 30px; text-align: center;">
          <img 
            src="${proxiedUrl}" 
            style="max-width: 400px; max-height: 300px; border-radius: 8px;" 
            onerror="this.style.display='none'"
            crossorigin="anonymous"
          />
        </div>
      `;
    }
  }

  // Add nutrition info
  if (recipe.nutrition) {
    html += `
      <div style="background-color: #f8f9fa; padding: 15px; border-radius: 8px; margin-bottom: 30px;">
        <h3 style="color: #333; margin-bottom: 10px; font-size: 18px;">Nutrition (per serving)</h3>
        <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; font-size: 14px;">
          <div>Calories: ${recipe.nutrition.calories || 0}</div>
          <div>Protein: ${recipe.nutrition.protein || 0}g</div>
          <div>Carbs: ${recipe.nutrition.carbs || 0}g</div>
          <div>Fat: ${recipe.nutrition.fat || 0}g</div>
          <div>Fiber: ${recipe.nutrition.fiber || 0}g</div>
          <div>Sugar: ${recipe.nutrition.sugar || 0}g</div>
        </div>
      </div>
    `;
  }

  // Add ingredients
  html += `
    <div style="margin-bottom: 30px;">
      <h3 style="color: #333; margin-bottom: 15px; font-size: 20px;">Ingredients</h3>
      <ul style="list-style: none; padding: 0;">
  `;
  
  if (recipe.ingredients && recipe.ingredients.length > 0) {
    recipe.ingredients.forEach((ing, index) => {
      if (ing.is_section_header) {
        html += `<li style="font-weight: bold; margin-top: 10px; margin-bottom: 5px; color: #555;">${ing.section_name}</li>`;
      } else {
        html += `
          <li style="margin-bottom: 8px; padding-left: ${ing.is_section_header ? '0' : '20px'};">
            ${ing.qty} ${ing.unit_name} ${ing.name} ${ing.suffix || ''}
          </li>
        `;
      }
    });
  }
  
  html += `
      </ul>
    </div>
  `;

  // Add instructions
  html += `
    <div>
      <h3 style="color: #333; margin-bottom: 15px; font-size: 20px;">Instructions</h3>
      <ol style="padding-left: 20px;">
  `;
  
  if (recipe.instructions && recipe.instructions.length > 0) {
    recipe.instructions.forEach((inst, index) => {
      if (inst.is_section_header) {
        html += `<div style="font-weight: bold; margin-top: 10px; margin-bottom: 5px; color: #555;">${inst.section_name}</div>`;
      } else {
        html += `<li style="margin-bottom: 12px; line-height: 1.6;">${inst.instruction}</li>`;
      }
    });
  }
  
  html += `
      </ol>
    </div>
  `;

  container.innerHTML = html;
  document.body.appendChild(container);
  
  return container;
};

// Main export function
export const exportRecipeBook = async (recipes, includeImages, onProgress) => {
  try {
    // Create new PDF document
    const pdf = new jsPDF('p', 'mm', 'a4');
    let pageNumber = 1;
    
    // Create title page
    onProgress(5, 'Creating title page...');
    createTitlePage(pdf, recipes[0]?.user_name);
    
    // Group recipes by course
    const recipesByCourse = recipes.reduce((acc, recipe) => {
      const course = recipe.course || 'Other';
      if (!acc[course]) acc[course] = [];
      acc[course].push(recipe);
      return acc;
    }, {});
    
    // Sort courses in a logical order
    const courseOrder = ['Appetizer', 'Breakfast', 'Lunch', 'Dinner', 'Entree', 'Dessert', 'Snack', 'Other'];
    const sortedCourses = Object.keys(recipesByCourse).sort((a, b) => {
      const indexA = courseOrder.indexOf(a);
      const indexB = courseOrder.indexOf(b);
      if (indexA === -1 && indexB === -1) return a.localeCompare(b);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
    
    let recipeCount = 0;
    const totalRecipes = recipes.length;
    
    // Process each course
    for (const course of sortedCourses) {
      // Add course divider page
      pdf.addPage();
      onProgress(10 + (recipeCount / totalRecipes * 80), `Creating ${course} section...`);
      createCourseDivider(pdf, course);
      
      // Process recipes in this course
      for (const recipe of recipesByCourse[course]) {
        recipeCount++;
        onProgress(10 + (recipeCount / totalRecipes * 80), `Processing ${recipe.name}...`);
        
        // Create recipe HTML
        const recipeElement = await createRecipeHTML(recipe, includeImages);
        
        try {
          // Try to convert HTML to canvas with images
          let canvas;
          try {
            canvas = await html2canvas(recipeElement, {
              scale: 2,
              logging: false,
              useCORS: true,
              allowTaint: false,
              imageTimeout: 5000
            });
          } catch (canvasError) {
            // If images cause issues, retry without them
            console.warn(`Failed to render recipe with images, retrying without: ${recipe.name}`);
            document.body.removeChild(recipeElement);
            const recipeElementNoImages = await createRecipeHTML(recipe, false);
            canvas = await html2canvas(recipeElementNoImages, {
              scale: 2,
              logging: false
            });
            document.body.appendChild(recipeElementNoImages);
            recipeElement = recipeElementNoImages;
          }
          
          // Add new page for recipe
          pdf.addPage();
          
          // Calculate dimensions
          const imgWidth = 190; // A4 width minus margins
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          
          // Add image to PDF
          let imgData;
          try {
            imgData = canvas.toDataURL('image/png');
          } catch (exportError) {
            // If export fails due to tainted canvas, retry without images
            console.warn(`Canvas tainted for recipe: ${recipe.name}, retrying without images`);
            document.body.removeChild(recipeElement);
            const recipeElementNoImages = await createRecipeHTML(recipe, false);
            const cleanCanvas = await html2canvas(recipeElementNoImages, {
              scale: 2,
              logging: false
            });
            imgData = cleanCanvas.toDataURL('image/png');
            document.body.removeChild(recipeElementNoImages);
            document.body.appendChild(recipeElement);
          }
          
          // If content is too tall, split across pages
          if (imgHeight > 270) { // A4 height minus margins
            let yPosition = 0;
            let remainingHeight = imgHeight;
            
            while (remainingHeight > 0) {
              if (yPosition > 0) pdf.addPage();
              
              const sourceY = (yPosition / imgHeight) * canvas.height;
              const sourceHeight = Math.min((270 / imgHeight) * canvas.height, canvas.height - sourceY);
              
              // Create a new canvas for this page portion
              const pageCanvas = document.createElement('canvas');
              pageCanvas.width = canvas.width;
              pageCanvas.height = sourceHeight;
              const ctx = pageCanvas.getContext('2d');
              ctx.drawImage(canvas, 0, sourceY, canvas.width, sourceHeight, 0, 0, canvas.width, sourceHeight);
              
              const pageImgData = pageCanvas.toDataURL('image/png');
              pdf.addImage(pageImgData, 'PNG', 10, 10, imgWidth, (sourceHeight * imgWidth) / canvas.width);
              
              yPosition += 270;
              remainingHeight -= 270;
            }
          } else {
            pdf.addImage(imgData, 'PNG', 10, 10, imgWidth, imgHeight);
          }
          
        } catch (error) {
          console.error('Error rendering recipe:', recipe.name, error);
          // Add text fallback
          pdf.text(`Error rendering recipe: ${recipe.name}`, 10, 20);
        } finally {
          // Clean up
          document.body.removeChild(recipeElement);
        }
      }
    }
    
    // Save the PDF
    onProgress(95, 'Finalizing PDF...');
    const fileName = `recipe-book-${new Date().toISOString().split('T')[0]}.pdf`;
    pdf.save(fileName);
    
    onProgress(100, 'Export complete!');
    
  } catch (error) {
    console.error('Error exporting recipe book:', error);
    throw error;
  }
};